import React, { useEffect, useState } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Controller, useWatch } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import FormWarningLabel from 'components/atoms/FormWarningLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import {
  IFormElementOptionHelperTextProp,
  IFormElementOptionProp,
  IFormElementProps,
} from 'models/form';
import { EDictionaryScope } from 'models/dictionaryScopeContext';
import { useArchivedOptions } from 'hooks/useArchivedOptions';
import { useArchivedValueConfirm } from 'hooks/useArchivedValueConfirm';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import { Tooltip } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { getNestedErrorMessage } from './helpers';

interface IFormRadioButtonGroup extends IFormElementProps {
  label: string;
  options: IFormElementOptionProp[];
  onChangeOption?: (arg: EventTarget) => void;
  booleanType?: boolean;
  withValidation?: boolean;
  helperTooltipText?: IFormElementOptionHelperTextProp[];
  required?: boolean;
  oneLine?: boolean;
  selectedByDefault?: string;
  dictionaryScope?: EDictionaryScope;
}
export const FormRadioGroup = ({
  name,
  label,
  options,
  control,
  errors,
  disabled,
  booleanType = false,
  withValidation = false,
  helperTooltipText,
  onChangeOption,
  required = false,
  oneLine = false,
  selectedByDefault,
  dictionaryScope,
}: IFormRadioButtonGroup) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { t } = useTranslations();
  const watchedValue = useWatch({ control, name });
  const { visibleOptions, hasArchivedSelection, isArchivedValue } = useArchivedOptions(
    options,
    watchedValue,
    dictionaryScope,
  );
  const { confirmChange, archivedConfirmModal } = useArchivedValueConfirm();

  useEffect(() => {
    let isError = false;
    const keys = name.split('.');
    const errMessage = getNestedErrorMessage(errors, keys);
    if (keys && errMessage) {
      isError = true;
      setErrorMessage(errMessage);
    }
    if (!isError) {
      setErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const checkedVal = (field: any, option: any): boolean | undefined => {
    if (booleanType) {
      if (field?.value === undefined || field?.value === null) return false;
      return field?.value.toString() === option.value.toString();
    }
    if (selectedByDefault && field.value === '')
      return option.value.toString() === selectedByDefault;
    return field?.value === option.value;
  };

  // eslint-disable-next-line consistent-return
  const getHelper = (value: string | number) => {
    const helper = helperTooltipText?.filter((item) => item.value === value);
    if (helper?.length) {
      return (
        <Tooltip
          title={helper[0].text}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#082a4a',
                fontWeight: 400,
                padding: '5px 15px',
                fontSize: '0.75rem',
                lineHeight: '1rem',
                maxWidth: '500px',
              },
            },
          }}
        >
          <div style={{ marginRight: '24px', marginLeft: '-8px', paddingTop: '7px' }}>
            <Icon type={EIconTypes.info2} color={colors.primary100} fontSize={18} />
          </div>
        </Tooltip>
      );
    }
  };

  return (
    <StyledFormElementWrapper oneLine={oneLine}>
      <FormLabel name={name} label={required ? `${label}*` : label} />
      <RadioGroup
        aria-labelledby={name}
        name={name}
        sx={{ flexDirection: 'row', alignItems: 'center' }}
      >
        {visibleOptions.map((option: IFormElementOptionProp) => (
          <>
            <Controller
              key={option.value.toString()}
              name={name}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  {...field}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: 14,
                      fontWeight: 400,
                      color: `${colors.black} !important`,
                    },
                  }}
                  disabled={disabled || isArchivedValue(option.value)}
                  value={option.value.toString()}
                  checked={checkedVal(field, option)}
                  onChange={(e) => {
                    const { target } = e;
                    const newValue = option.value.toString();
                    confirmChange(hasArchivedSelection, () => {
                      field.onChange(newValue);
                      if (onChangeOption) {
                        onChangeOption(target);
                      }
                    });
                  }}
                  control={
                    <Radio
                      size="small"
                      id={`${name}-${option.value}`}
                      sx={{
                        color: colors.gray400,
                        '&.Mui-checked': {
                          color: disabled ? colors.gray400 : colors.primary700,
                        },
                      }}
                    />
                  }
                  label={
                    isArchivedValue(option.value)
                      ? `${option.label} ${t('dictionary.archivedValue.option.suffix')}`
                      : option.label
                  }
                />
              )}
            />
            {helperTooltipText ? getHelper(option.value) : ''}
          </>
        ))}
      </RadioGroup>
      {hasArchivedSelection && (
        <FormWarningLabel name={name} label={t('dictionary.archivedValue.warning')} />
      )}
      {(withValidation || errorMessage) && <FormErrorLabel label={errorMessage} />}
      {archivedConfirmModal}
    </StyledFormElementWrapper>
  );
};
